/**
 * Single SignalR hub subscription for pending-approvals push; exposes connection state for hybrid list refresh.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useApprovalsPush } from '../hooks/useApprovalsPush';
import { useApprovalsPushEligibility } from '../hooks/useApprovalsPushEligibility';

export interface ApprovalsPushContextValue {
  /** Hub TCP/WebSocket is Connected. */
  isConnected: boolean;
  /** We attempt a hub connection (role + permission + user id). */
  hubEnabled: boolean;
  notifyAnimationComplete: () => void;
  /** Navigation registers banner/bell animation; returns unregister. */
  registerPushAnimationListener: (fn: () => void) => () => void;
}

const ApprovalsPushContext = createContext<ApprovalsPushContextValue | null>(null);

export function ApprovalsPushProvider({ children }: { children: React.ReactNode }) {
  const { showNotifications, numericUserId } = useApprovalsPushEligibility();
  const hubEnabled = showNotifications && numericUserId != null && numericUserId > 0;

  const animationListenersRef = useRef(new Set<() => void>());

  const registerPushAnimationListener = useCallback((fn: () => void) => {
    animationListenersRef.current.add(fn);
    return () => {
      animationListenersRef.current.delete(fn);
    };
  }, []);

  const onPushReceived = useCallback(() => {
    animationListenersRef.current.forEach((fn) => {
      fn();
    });
  }, []);

  const { isConnected, notifyAnimationComplete } = useApprovalsPush({
    enabled: hubEnabled,
    onPushReceived,
  });

  const value = useMemo<ApprovalsPushContextValue>(
    () => ({
      isConnected,
      hubEnabled,
      notifyAnimationComplete,
      registerPushAnimationListener,
    }),
    [isConnected, hubEnabled, notifyAnimationComplete, registerPushAnimationListener]
  );

  return <ApprovalsPushContext.Provider value={value}>{children}</ApprovalsPushContext.Provider>;
}

export function useApprovalsPushContext(): ApprovalsPushContextValue {
  const ctx = useContext(ApprovalsPushContext);
  if (ctx == null) {
    throw new Error('useApprovalsPushContext must be used within ApprovalsPushProvider');
  }
  return ctx;
}
